import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CalendarClock, CalendarDays, RefreshCw, CalendarPlus, ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const PERIOD_ICONS = [CalendarClock, CalendarDays, RefreshCw, CalendarPlus];
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/7dfacbbb-8e0d-4602-a3aa-7ff35c27b573/images/669469f70726961ee2cefc1101be053e9dba5eb14791f9d1d0da0bb8d116cc71.jpeg";

export const MedicareEnrollmentPage = () => {
  const { t } = useTranslation();
  const periods = t('medicareEnrollment.periods', { returnObjects: true }) || [];

  useEffect(() => {
    document.title = 'Medicare Enrollment Periods | Calyvra Insurance';
  }, []);

  return (
    <div className="overflow-hidden" data-testid="medicare-enrollment-page">
      {/* Hero */}
      <section className="gradient-hero py-16 lg:py-24">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 rounded-full mb-6">
                <ShieldCheck className="h-4 w-4 text-sky-600" />
                <span className="text-sm font-medium text-sky-700">{t('medicareEnrollment.badge')}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                {t('medicareEnrollment.heroTitle')}{' '}
                <span className="text-sky-500">{t('medicareEnrollment.heroTitleHighlight')}</span>
              </h1>
              <p className="text-lg text-slate-600">{t('medicareEnrollment.heroDesc')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={HERO_IMAGE}
                  alt="Happy senior couple exploring their Medicare options"
                  className="w-full h-auto object-cover aspect-[4/3]"
                  data-testid="medicare-enrollment-hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Periods */}
      <section className="section-spacing bg-white" data-testid="medicare-periods">
        <div className="container-custom">
          <p className="text-slate-700 font-medium mb-8 text-center">{t('medicareEnrollment.periodsLabel')}</p>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {periods.map((p, index) => {
              const Icon = PERIOD_ICONS[index] || CalendarDays;
              return (
                <motion.div
                  key={p.code}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="bg-white p-7 rounded-2xl border border-slate-100 card-shadow hover:shadow-lg transition-shadow"
                  data-testid={`medicare-period-${p.code}`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="h-12 w-12 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-6 w-6 text-sky-500" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-sky-600 tracking-wide">{p.code}</span>
                      <h3 className="text-lg font-semibold text-slate-900 leading-tight">{p.name}</h3>
                    </div>
                  </div>
                  <div className="inline-block text-sm font-medium text-slate-700 bg-slate-50 rounded-lg px-3 py-1.5 mb-3">
                    {p.window}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">{p.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900" data-testid="medicare-enrollment-cta">
        <div className="container-custom text-center max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('medicareEnrollment.ctaTitle')}</h2>
          <p className="text-slate-300 text-lg mb-8">{t('medicareEnrollment.ctaDesc')}</p>
          <Link to="/contact">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="medicare-enrollment-cta-btn">
              {t('medicareEnrollment.ctaButton')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <p className="text-slate-500 text-xs mt-10 leading-relaxed max-w-3xl mx-auto">
            {t('medicareEnrollment.disclaimer')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default MedicareEnrollmentPage;
